import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";

export interface FlowchestAiImportStartRequest {
  videoUrl: string;
  confirmRights: boolean;
  sourceTitle?: string;
  sourceNotes?: string;
}

export interface FlowchestAiImportSegment {
  stepIndex: number;
  title: string;
  description: string;
  startSec: number;
  endSec: number;
  videoUrl?: string;
  posterUrl?: string;
}

export interface FlowchestAiImportComponent {
  itemkey: string;
  itemValue: string;
  groupName?: string;
  wheretofindit?: string;
  url?: string;
}

export interface FlowchestAiImportDraft {
  title: string;
  description: string;
  coverUrl: string;
  coverPosterUrl: string;
  duration: number;
  durationUnit: string;
  searchKeyWords: string;
  steps: FlowchestAiImportSegment[];
  components: FlowchestAiImportComponent[];
  suggestedComponents: string[];
  stepClipsGenerated?: number;
  useSegmentPlayback?: boolean;
  warningMessage?: string;
  transcriptionSuccessCount?: number;
  transcriptionFailureCount?: number;
}

export interface FlowchestAiImportJobResponse {
  jobId: string;
  status: string;
  progressPercent: number;
  statusMessage?: string;
  errorMessage?: string;
  coinsRequired?: number;
  planCoinsReserved?: number;
  topUpCoinsReserved?: number;
  coinsCharged?: boolean;
  result?: FlowchestAiImportDraft;
}

@Injectable({ providedIn: "root" })
export class FlowchestAiImportService {
  private readonly base = environment.apiBaseUrl + "api/FlowchestAiImport";

  constructor(private http: HttpClient) {}

  start(request: FlowchestAiImportStartRequest): Observable<FlowchestAiImportJobResponse> {
    return this.http
      .post<any>(`${this.base}/start`, request)
      .pipe(map((res) => this.normalizeJob(res)));
  }

  startUpload(
    file: File,
    confirmRights: boolean,
    sourceTitle?: string,
    sourceNotes?: string,
    videoDurationSec?: number,
    sourceDocument?: File | null,
    splitMode?: string,
    maxClipSec?: number
  ): Observable<FlowchestAiImportJobResponse> {
    const form = new FormData();
    form.append("videoFile", file);
    form.append("confirmRights", String(confirmRights));
    if (sourceTitle?.trim()) {
      form.append("sourceTitle", sourceTitle.trim());
    }
    if (sourceNotes?.trim()) {
      form.append("sourceNotes", sourceNotes.trim());
    }
    if (videoDurationSec && videoDurationSec > 0) {
      form.append("videoDurationSec", String(videoDurationSec));
    }
    if (splitMode?.trim()) {
      form.append("splitMode", splitMode.trim());
    }
    if (maxClipSec && maxClipSec > 0) {
      form.append("maxClipSec", String(maxClipSec));
    }
    if (sourceDocument) {
      form.append("sourceDocument", sourceDocument, sourceDocument.name);
    }
    return this.http
      .post<any>(`${this.base}/start-upload`, form)
      .pipe(map((res) => this.normalizeJob(res)));
  }

  getJob(jobId: string): Observable<FlowchestAiImportJobResponse> {
    return this.http
      .get<any>(`${this.base}/${jobId}`)
      .pipe(map((res) => this.normalizeJob(res)));
  }

  generateFromSteps(request: {
    confirmRights: boolean;
    sourceTitle?: string;
    sourceNotes?: string;
    coverUrl?: string;
    coverPosterUrl?: string;
    steps: Array<{
      stepIndex: number;
      videoUrl: string;
      posterUrl?: string;
      title?: string;
      description?: string;
      mediaType?: "image" | "video";
      file?: File | null;
    }>;
    sourceDocument?: File | null;
  }): Observable<FlowchestAiImportJobResponse | FlowchestAiImportDraft> {
    const hasFiles = request.steps.some((s) => s.file);
    const hasVideoUrls = request.steps.some((s) => !!s.videoUrl?.trim());
    if (request.sourceDocument || hasFiles || hasVideoUrls) {
      const form = new FormData();
      form.append("confirmRights", String(request.confirmRights));
      if (request.sourceTitle?.trim()) {
        form.append("sourceTitle", request.sourceTitle.trim());
      }
      if (request.sourceNotes?.trim()) {
        form.append("sourceNotes", request.sourceNotes.trim());
      }
      if (request.coverUrl) {
        form.append("coverUrl", request.coverUrl);
      }
      if (request.coverPosterUrl) {
        form.append("coverPosterUrl", request.coverPosterUrl);
      }
      form.append(
        "stepsJson",
        JSON.stringify(
          request.steps.map((step) => ({
            stepIndex: step.stepIndex,
            videoUrl: step.videoUrl,
            posterUrl: step.posterUrl,
            title: step.title,
            description: step.description,
            mediaType: step.mediaType || "video",
          }))
        )
      );
      if (request.sourceDocument) {
        form.append("sourceDocument", request.sourceDocument, request.sourceDocument.name);
      }
      const stepVideoFiles: File[] = [];
      const stepVideoFileStepIndexes: number[] = [];
      request.steps.forEach((step) => {
        if (step.file) {
          stepVideoFiles.push(step.file);
          stepVideoFileStepIndexes.push(step.stepIndex);
        }
      });
      if (stepVideoFileStepIndexes.length > 0) {
        form.append("stepVideoFileStepIndexes", JSON.stringify(stepVideoFileStepIndexes));
      }
      stepVideoFiles.forEach((file) => {
        form.append("stepVideoFiles", file, file.name);
      });
      return this.http
        .post<any>(`${this.base}/generate-from-steps-upload`, form)
        .pipe(map((res) => this.normalizeJob(res)));
    }

    return this.http
      .post<any>(`${this.base}/generate-from-steps`, request)
      .pipe(map((res) => this.normalizeDraft(res)));
  }

  uploadSupportingReferenceDocument(file: File): Observable<{ fileName: string }> {
    const form = new FormData();
    form.append("fileData", file);
    return this.http
      .post<any>(environment.apiBaseUrl + environment.uploadDocumentsUrl, form)
      .pipe(
        map((res) => {
          const status = res?.status ?? res?.Status;
          const fileName = res?.fileName ?? res?.FileName ?? "";
          if (status === false || !fileName) {
            throw new Error(res?.message ?? res?.Message ?? "Upload failed");
          }
          return { fileName };
        })
      );
  }

  private normalizeJob(res: any): FlowchestAiImportJobResponse {
    if (!res) {
      return {
        jobId: "",
        status: "Failed",
        progressPercent: 0,
      };
    }

    const rawResult = res.result ?? res.Result;
    return {
      jobId: res.jobId ?? res.JobId ?? "",
      status: res.status ?? res.Status ?? "",
      progressPercent: res.progressPercent ?? res.ProgressPercent ?? 0,
      statusMessage: res.statusMessage ?? res.StatusMessage,
      errorMessage: res.errorMessage ?? res.ErrorMessage,
      coinsRequired: res.coinsRequired ?? res.CoinsRequired,
      planCoinsReserved: res.planCoinsReserved ?? res.PlanCoinsReserved,
      topUpCoinsReserved: res.topUpCoinsReserved ?? res.TopUpCoinsReserved,
      coinsCharged: res.coinsCharged ?? res.CoinsCharged,
      result: rawResult ? this.normalizeDraft(rawResult) : undefined,
    };
  }

  private normalizeDraft(d: any): FlowchestAiImportDraft {
    const steps = (d.steps ?? d.Steps ?? []).map((s: any) => ({
      stepIndex: s.stepIndex ?? s.StepIndex ?? 0,
      title: s.title ?? s.Title ?? "",
      description: s.description ?? s.Description ?? "",
      startSec: s.startSec ?? s.StartSec ?? 0,
      endSec: s.endSec ?? s.EndSec ?? 0,
      videoUrl: s.videoUrl ?? s.VideoUrl,
      posterUrl: s.posterUrl ?? s.PosterUrl,
    }));

    const rawComponents = d.components ?? d.Components ?? [];
    const components = rawComponents.map((c: any) => ({
      itemkey: (c.itemkey ?? c.Itemkey ?? c.itemKey ?? c.itemName ?? "").trim(),
      itemValue: (c.itemValue ?? c.ItemValue ?? c.itemQuantity ?? "").trim(),
      groupName: c.groupName ?? c.GroupName ?? "",
      wheretofindit: c.wheretofindit ?? c.Wheretofindit ?? "",
      url: c.url ?? c.Url ?? "",
    })).filter((c: FlowchestAiImportComponent) => c.itemkey.length > 0);

    const suggestedComponents = d.suggestedComponents ?? d.SuggestedComponents ?? [];

    return {
      title: d.title ?? d.Title ?? "",
      description: d.description ?? d.Description ?? "",
      coverUrl: d.coverUrl ?? d.CoverUrl ?? "",
      coverPosterUrl: d.coverPosterUrl ?? d.CoverPosterUrl ?? "",
      duration: d.duration ?? d.Duration ?? 0,
      durationUnit: d.durationUnit ?? d.DurationUnit ?? "Mins",
      searchKeyWords: d.searchKeyWords ?? d.SearchKeyWords ?? "",
      steps,
      components: components.length > 0
        ? components
        : suggestedComponents.map((name: string) => ({
            itemkey: name,
            itemValue: "",
            groupName: "",
            wheretofindit: "",
            url: "",
          })),
      suggestedComponents,
      stepClipsGenerated: d.stepClipsGenerated ?? d.StepClipsGenerated,
      useSegmentPlayback: d.useSegmentPlayback ?? d.UseSegmentPlayback ?? false,
      warningMessage: d.warningMessage ?? d.WarningMessage,
      transcriptionSuccessCount: d.transcriptionSuccessCount ?? d.TranscriptionSuccessCount,
      transcriptionFailureCount: d.transcriptionFailureCount ?? d.TranscriptionFailureCount,
    };
  }
}
